"use client"

import * as React from "react"

import type { CreateKind, CreateOverlayLayout, CreatePrefill } from "@/features/create-overlay/model/create-kind"

/** What a form tells the host so the shared chrome (footer, close guard) can react. */
export type CreateFormState = {
  /** Unsaved input: closing asks for confirmation. */
  dirty: boolean
  /** Saving: the primary button spins and Annuler is disabled. */
  pending: boolean
  /** The form is not ready to submit yet (a required first step not taken). */
  submitDisabled: boolean
  /**
   * A form with steps: the primary button's label for this step ("Suivant"), instead of the kind's save label.
   * The primary button still submits the form; the form decides whether that advances or saves.
   */
  submitLabel?: string | null
  /** A form with steps, past its first one: shows "Précédent", which calls this. Keep it referentially stable. */
  back?: (() => void) | null
  /**
   * A second way to save, beside the primary button (a transfer request: "Enregistrer le brouillon" next to
   * "Envoyer pour approbation"). Keep it referentially stable (memoised).
   */
  secondary?: CreateFormSecondaryAction | null
}

export type CreateFormSecondaryAction = {
  label: string
  onClick: () => void
  disabled?: boolean
}

export const INITIAL_CREATE_FORM_STATE: CreateFormState = {
  dirty: false,
  pending: false,
  submitDisabled: false,
  submitLabel: null,
  back: null,
  secondary: null,
}

export type CreateOverlayResult = {
  /** Replace the overlay's history entry with this page (the created record). Omit to stay on the current page. */
  href?: string
}

export type CreateOverlayFormContextValue = {
  kind: CreateKind
  layout: CreateOverlayLayout
  isMobile: boolean
  /** The id the form element must carry: the footer's primary button submits it (`<button form={formId}>`). */
  formId: string
  prefill: CreatePrefill | null
  report: (state: CreateFormState) => void
  /**
   * Call after a successful save (after the toast). The host then resets the form when "En créer un autre après"
   * is ticked, else replaces the overlay with `result.href` or closes it.
   */
  complete: (result?: CreateOverlayResult) => void
  /** Desktop "C": the right-hand panel the preview is drawn into (null elsewhere, or before it mounts). */
  previewContainer: HTMLElement | null
}

const CreateOverlayFormContext = React.createContext<CreateOverlayFormContextValue | null>(null)

export const CreateOverlayFormProvider = CreateOverlayFormContext.Provider

export function useCreateOverlayForm(): CreateOverlayFormContextValue {
  const value = React.useContext(CreateOverlayFormContext)
  if (!value) throw new Error("useCreateOverlayForm must be used inside the create overlay host")
  return value
}

/** Keeps the host in sync with the form's dirty / pending / ready state. */
export function useReportCreateFormState(state: CreateFormState) {
  const { report } = useCreateOverlayForm()
  const { dirty, pending, submitDisabled, submitLabel = null, back = null, secondary = null } = state
  React.useEffect(() => {
    report({ dirty, pending, submitDisabled, submitLabel, back, secondary })
  }, [back, dirty, pending, report, secondary, submitDisabled, submitLabel])
}

/**
 * The same report as a component, for a shared form body that only sometimes renders inside the overlay (it
 * cannot call the hook conditionally, but it can render this conditionally).
 */
export function CreateFormStateReporter(props: CreateFormState) {
  useReportCreateFormState(props)
  return null
}
